// Hummin — WebGL atmospheric noise canvas
// Reusable across hero + footer. Curl-noise drift, dark base, slow.
// Mounts on any canvas with [data-noise-canvas]. Reads optional data attrs:
//   data-noise-base="rgb"      — base color (default "20 20 22")
//   data-noise-tint="rgb"      — tint color (default "60 70 80")
//   data-noise-scale="0.6"     — noise spatial scale (default 0.7)
//   data-noise-speed="0.04"    — drift speed (default 0.05)
// Desktop only (>=1024px) + GPU available + reduced-motion respected.
// Falls back gracefully — canvas stays empty if WebGL not supported.

(() => {
  const isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isDesktop = () => window.innerWidth >= 1024;

  const VERT = `
    attribute vec2 a_pos;
    void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
  `;

  // Curl-noise-driven gradient. Subtle, dark, atmospheric.
  const FRAG = `
    precision mediump float;
    uniform vec2 u_res;
    uniform float u_t;
    uniform vec3 u_base;
    uniform vec3 u_tint;
    uniform float u_scale;
    uniform float u_speed;

    // Hash + noise (Inigo Quilez style)
    float hash(vec2 p) {
      return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
    }
    float noise(vec2 p) {
      vec2 i = floor(p);
      vec2 f = fract(p);
      vec2 u = f * f * (3.0 - 2.0 * f);
      return mix(
        mix(hash(i + vec2(0,0)), hash(i + vec2(1,0)), u.x),
        mix(hash(i + vec2(0,1)), hash(i + vec2(1,1)), u.x),
        u.y
      );
    }
    float fbm(vec2 p) {
      float v = 0.0;
      float a = 0.5;
      for (int i = 0; i < 4; i++) {
        v += a * noise(p);
        p *= 2.0;
        a *= 0.5;
      }
      return v;
    }

    void main() {
      vec2 uv = gl_FragCoord.xy / u_res.xy;
      float aspect = u_res.x / u_res.y;
      uv.x *= aspect;

      float t = u_t * u_speed;
      vec2 q = uv * u_scale;
      float n = fbm(q + vec2(t * 0.6, t * 0.4));
      float m = fbm(q + vec2(-t * 0.3, t * 0.7) + n * 0.8);

      vec3 col = mix(u_base, u_tint, m * 0.6);
      // Vignette
      vec2 vc = uv - vec2(aspect * 0.5, 0.5);
      float vig = 1.0 - dot(vc, vc) * 0.4;
      col *= vig;

      gl_FragColor = vec4(col / 255.0, 1.0);
    }
  `;

  const compileShader = (gl, src, type) => {
    const sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      gl.deleteShader(sh);
      return null;
    }
    return sh;
  };

  const buildProgram = (gl) => {
    const v = compileShader(gl, VERT, gl.VERTEX_SHADER);
    const f = compileShader(gl, FRAG, gl.FRAGMENT_SHADER);
    if (!v || !f) return null;
    const p = gl.createProgram();
    gl.attachShader(p, v);
    gl.attachShader(p, f);
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      gl.deleteProgram(p);
      return null;
    }
    return p;
  };

  const parseRgb = (str, fallback) => {
    if (!str) return fallback;
    const parts = str.split(/[\s,]+/).map((s) => parseFloat(s));
    if (parts.length === 3 && parts.every((n) => !isNaN(n))) return parts;
    return fallback;
  };

  const initCanvas = (canvas) => {
    if (isReducedMotion || !isDesktop()) return;

    const gl = canvas.getContext('webgl', { alpha: true, antialias: false, premultipliedAlpha: false });
    if (!gl) return;

    const program = buildProgram(gl);
    if (!program) return;
    gl.useProgram(program);

    // Full-screen quad
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(program, 'a_pos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const uRes = gl.getUniformLocation(program, 'u_res');
    const uT = gl.getUniformLocation(program, 'u_t');
    const uBase = gl.getUniformLocation(program, 'u_base');
    const uTint = gl.getUniformLocation(program, 'u_tint');
    const uScale = gl.getUniformLocation(program, 'u_scale');
    const uSpeed = gl.getUniformLocation(program, 'u_speed');

    const base = parseRgb(canvas.dataset.noiseBase, [20, 20, 22]);
    const tint = parseRgb(canvas.dataset.noiseTint, [60, 70, 80]);
    const scale = parseFloat(canvas.dataset.noiseScale || '0.7');
    const speed = parseFloat(canvas.dataset.noiseSpeed || '0.05');

    gl.uniform3f(uBase, base[0], base[1], base[2]);
    gl.uniform3f(uTint, tint[0], tint[1], tint[2]);
    gl.uniform1f(uScale, scale);
    gl.uniform1f(uSpeed, speed);

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const w = Math.floor(canvas.clientWidth * dpr);
      const h = Math.floor(canvas.clientHeight * dpr);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
        gl.uniform2f(uRes, w, h);
      }
    };

    let start = performance.now();
    let visible = true;
    const loop = (now) => {
      if (!visible) return;
      resize();
      gl.uniform1f(uT, (now - start) / 1000);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);

    // Pause when offscreen — saves GPU
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !visible) {
            visible = true;
            requestAnimationFrame(loop);
          } else if (!entry.isIntersecting) {
            visible = false;
          }
        });
      }, { threshold: 0 });
      io.observe(canvas);
    }
  };

  const init = () => {
    document.querySelectorAll('[data-noise-canvas]').forEach(initCanvas);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
